import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export type ConfirmTone = 'default' | 'destructive';

export function canConfirmAction(opts: {
  requireReason: boolean;
  reason: string;
  busy: boolean;
}): boolean {
  if (opts.busy) return false;
  if (opts.requireReason && opts.reason.trim().length === 0) return false;
  return true;
}

export interface ConfirmActionDialogBodyProps {
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  requireReason?: boolean;
  collectReason?: boolean;
  reasonLabel?: string;
  reason: string;
  onReasonChange: (value: string) => void;
  busy: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export const ConfirmActionDialogBody: React.FC<ConfirmActionDialogBodyProps> = ({
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  requireReason = false,
  collectReason = false,
  reasonLabel = 'Reason',
  reason,
  onReasonChange,
  busy,
  error,
  onCancel,
  onConfirm,
  secondaryLabel,
  onSecondary,
}) => (
  <div className="flex flex-col gap-3">
    {requireReason || collectReason ? (
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-slate-700">{reasonLabel}</span>
        <Textarea
          value={reason}
          onChange={(event) => onReasonChange(event.target.value)}
          rows={3}
          disabled={busy}
          className="text-xs"
        />
      </label>
    ) : null}
    {error ? <p className="text-xs text-red-600">{error}</p> : null}
    <div className="flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
        {cancelLabel}
      </Button>
      {secondaryLabel && onSecondary ? (
        <Button type="button" variant="outline" onClick={onSecondary} disabled={busy}>
          {busy ? 'Working…' : secondaryLabel}
        </Button>
      ) : null}
      <Button
        type="button"
        variant={tone === 'destructive' ? 'destructive' : 'default'}
        onClick={onConfirm}
        disabled={!canConfirmAction({ requireReason, reason, busy })}
      >
        {busy ? 'Working…' : confirmLabel}
      </Button>
    </div>
  </div>
);

export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  requireReason?: boolean;
  collectReason?: boolean;
  reasonLabel?: string;
  onConfirm: (reason?: string) => void | Promise<void>;
  secondaryLabel?: string;
  onSecondary?: (reason?: string) => void | Promise<void>;
}

export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onSecondary,
  requireReason = false,
  collectReason = false,
  ...bodyProps
}) => {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const handleAction = async (action: (reason?: string) => void | Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action(requireReason || collectReason ? reason.trim() : undefined);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <AlertDialogContent className="admin-density">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <ConfirmActionDialogBody
          {...bodyProps}
          requireReason={requireReason}
          collectReason={collectReason}
          reason={reason}
          onReasonChange={setReason}
          busy={busy}
          error={error}
          onCancel={() => onOpenChange(false)}
          onConfirm={() => { void handleAction(onConfirm); }}
          secondaryLabel={bodyProps.secondaryLabel}
          onSecondary={onSecondary ? () => { void handleAction(onSecondary); } : undefined}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
};
